import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Scrolling microphone level meter for the dictation composer.
 * Inspired by audiocn's BarVisualizer (paint on a frame loop, no React re-render),
 * reshaped to the product capture: thin grey bars entering from the right, slots
 * not yet filled shown as dots on the left. Pure canvas, no runtime dependency.
 */

const BAR_WIDTH_PX = 2;
const BAR_GAP_PX = 2;
const DOT_SIZE_PX = 2;
const SAMPLE_INTERVAL_MS = 60;
const REDUCED_MOTION_SAMPLE_INTERVAL_MS = 250;
/** Speech RMS rarely exceeds ~0.3; the curve lifts quiet voices without clipping loud ones. */
const LEVEL_GAIN = 3.2;

function readLevel(analyser: AnalyserNode, buffer: Float32Array<ArrayBuffer>) {
  analyser.getFloatTimeDomainData(buffer);
  let sum = 0;
  for (const sample of buffer) {
    sum += sample * sample;
  }
  return Math.min(1, Math.sqrt(Math.sqrt(sum / buffer.length) * LEVEL_GAIN));
}

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function ChatDictationWaveform({
  analyser,
  className,
}: {
  analyser: AnalyserNode | null;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return;
    }

    const step = BAR_WIDTH_PX + BAR_GAP_PX;
    const levels: number[] = [];
    const buffer = analyser ? new Float32Array(analyser.fftSize) : null;
    const interval = prefersReducedMotion() ? REDUCED_MOTION_SAMPLE_INTERVAL_MS : SAMPLE_INTERVAL_MS;
    let width = 0;
    let height = 0;
    let color = '';
    let lastSampleAt = 0;
    let frame = 0;

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      color = getComputedStyle(canvas).color;
    };

    const paint = () => {
      const slots = Math.max(1, Math.floor(width / step));
      const midY = height / 2;
      context.clearRect(0, 0, width, height);
      context.fillStyle = color;
      for (let slot = 0; slot < slots; slot += 1) {
        const x = width - (slots - slot) * step;
        const level = levels[levels.length - (slots - slot)];
        if (level === undefined) {
          context.beginPath();
          context.arc(x + BAR_WIDTH_PX / 2, midY, DOT_SIZE_PX / 2, 0, Math.PI * 2);
          context.fill();
          continue;
        }
        const barHeight = Math.max(DOT_SIZE_PX, level * height);
        context.beginPath();
        if (typeof context.roundRect === 'function') {
          context.roundRect(x, midY - barHeight / 2, BAR_WIDTH_PX, barHeight, BAR_WIDTH_PX / 2);
        } else {
          context.rect(x, midY - barHeight / 2, BAR_WIDTH_PX, barHeight);
        }
        context.fill();
      }
      if (levels.length > slots) {
        levels.splice(0, levels.length - slots);
      }
    };

    const tick = (now: number) => {
      if (analyser && buffer && now - lastSampleAt >= interval) {
        lastSampleAt = now;
        levels.push(readLevel(analyser, buffer));
        paint();
      }
      frame = requestAnimationFrame(tick);
    };

    resize();
    paint();
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
        resize();
        paint();
      })
      : null;
    observer?.observe(canvas);
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [analyser]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn('block h-7 w-full text-foreground/35 dark:text-foreground/45', className)}
      data-testid="chat-dictation-waveform"
    />
  );
}
